import { useEffect, useRef } from "react";
import { Box, LinearProgress, Typography } from "@mui/material";
import ReceiptScanningImage from "assets/images/scanning-file.png";
import { useAuth } from "@frontegg/react";
import httpClient from "utils/httpClient";
import { buildReceiptUploadFormData } from "utils/receipts/receiptUpload";
import { useTranslation } from "react-i18next";
import type { ReceiptUploadResult } from "./PostUploadPage";

interface ReceiptScanningProps {
  files: File[];
  onComplete: (result: ReceiptUploadResult) => void;
}

const ReceiptScanning: React.FC<ReceiptScanningProps> = ({
  files,
  onComplete,
}) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;

  useEffect(() => {
    if (files.length === 0) {
      onCompleteRef.current({
        QUEUED: [],
        FAILED: [],
      });
      return;
    }

    const accessToken = user?.accessToken;
    if (!accessToken) {
      // Fail closed, since the scanning screen cannot be dismissed.
      onCompleteRef.current({
        QUEUED: [],
        FAILED: files.map((file) => ({ filename: file.name })),
      });
      return;
    }

    const abortController = new AbortController();

    const uploadAll = async () => {
      const filteredReceipts: ReceiptUploadResult = {
        QUEUED: [],
        FAILED: [],
      };

      for (const file of files) {
        if (abortController.signal.aborted) return;

        try {
          await httpClient.post(
            "/api/v1/receipt/new",
            buildReceiptUploadFormData({ files: [file] }),
            {
              headers: {
                Authorization: `Bearer ${accessToken}`,
                Prefer: "respond-async",
              },
              signal: abortController.signal,
            },
          );

          filteredReceipts.QUEUED.push({ filename: file.name });
        } catch (error) {
          if (abortController.signal.aborted) return;
          console.error("Receipt upload failed:", error);
          filteredReceipts.FAILED.push({ filename: file.name });
        }
      }

      if (!abortController.signal.aborted) {
        onCompleteRef.current(filteredReceipts);
      }
    };

    void uploadAll();

    return () => {
      abortController.abort();
    };
  }, [files, user?.accessToken]);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: "var(--space-5)",
        flex: 1,
        overflowY: "auto",
      }}
    >
      <Box
        sx={{ display: "flex", flexDirection: "column", gap: "var(--space-5)" }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "var(--space-2)",
          }}
        >
          <Typography variant="h5" component="span">
            {t("Receipts scanning")}
          </Typography>
          <Typography variant="body2" component="span">
            {t("UplaodingImageDescription")}
          </Typography>
        </Box>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: "100%",
          }}
        >
          <Box
            component="img"
            src={ReceiptScanningImage}
            alt="Receipt scanning"
          />
        </Box>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "var(--space-3)",
          }}
        >
          <LinearProgress
            variant="indeterminate"
            color="success"
            sx={{ height: 12, borderRadius: "var(--radius-full)" }}
          />
          <Typography variant="body2" component="span">
            {t("Scanning receipts")}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default ReceiptScanning;
